import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  reloadCurrentUser,
  type CurrentUser,
} from "../../../../domains/user/hooks/useCurrentUser";
import { usePushNotificationSwitch } from "../../../../domains/notification/hooks/usePushNotificationSwitch";
import { apiClient } from "../../../../services/api";
import { displayToast } from "../../../../components/app/AppToast";
import { Switch } from "../../../../components/ui/Switch";
import {
  SettingsPage,
  SettingsRow,
  SettingsSection,
} from "../components/SettingsPanel";

/**
 * Notifications (Figma "D3.settings.notifications", "…permission" and
 * "…blocked"): one switch for the browser, one for the emails.
 *
 * The browser switch goes through the same push switch as everywhere else: it
 * asks the browser the first time, and a refusal can only be undone in the
 * browser's site settings, so the switch is then disabled and says where.
 *
 * Juisci staff also get the switch of the experimental features: it turns the
 * GrowthBook flag `EXPERIMENTAL` on for their account (see `App.tsx`).
 *
 * Sound and haptics are not drawn: `tools/ios.ts` reads them inside the iOS
 * app only, so in a browser they change nothing.
 */
export function NotificationsSection({
  currentUser,
}: {
  currentUser: CurrentUser;
}): React.JSX.Element {
  const { t } = useTranslation();
  const push = usePushNotificationSwitch();
  // useForm only for an in-flight flag, without owning `useState`: a flip
  // waits on the browser prompt or the account write before the next.
  const { handleSubmit, formState } = useForm();
  // `emailOptOut` is nullable, and a null has never opted out.
  const emailOn = currentUser.emailOptOut !== true;

  async function setEmail(on: boolean): Promise<void> {
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: { emailOptOut: !on },
      });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    await reloadCurrentUser();
  }

  async function setExperimentalFeatures(on: boolean): Promise<void> {
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: { experimentalFeatures: on },
      });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    await reloadCurrentUser();
  }

  const browserSubtitle = push.denied
    ? t(
        "Blocked in this browser. Allow notifications for {{site}} in your browser's site settings. Emails still work.",
        { site: window.location.hostname }
      )
    : push.needsOsPrompt
      ? t("Off. Turning it on asks your browser for permission.")
      : t("Visual abstracts, collections you follow and news from Juisci");

  return (
    <SettingsPage back={null}>
      <SettingsSection title={t("Notifications")} description={null}>
        <SettingsRow
          leading={null}
          title={t("Browser notifications")}
          subtitle={browserSubtitle}
          right={
            <Switch
              checked={push.enabled}
              label={t("Browser notifications")}
              disabled={push.denied || formState.isSubmitting}
              onCheckedChange={() => {
                void handleSubmit(async () => {
                  await push.toggleAndWait();
                })();
              }}
            />
          }
        />
        <SettingsRow
          leading={null}
          title={t("Emails")}
          subtitle={currentUser.email}
          right={
            <Switch
              checked={emailOn}
              label={t("Emails")}
              disabled={formState.isSubmitting}
              onCheckedChange={(on) => {
                void handleSubmit(() => setEmail(on))();
              }}
            />
          }
        />
      </SettingsSection>
      {currentUser.experimentalFeatures === null ? null : (
        <SettingsSection title={t("Juisci staff")} description={null}>
          <SettingsRow
            leading={null}
            title={t("Experimental features")}
            subtitle={t("Features still in test. They can change or break.")}
            right={
              <Switch
                checked={currentUser.experimentalFeatures}
                label={t("Experimental features")}
                disabled={formState.isSubmitting}
                onCheckedChange={(on) => {
                  void handleSubmit(() => setExperimentalFeatures(on))();
                }}
              />
            }
          />
        </SettingsSection>
      )}
    </SettingsPage>
  );
}
